import { Action, createReducer, on } from '@ngrx/store';
import * as actions from './actions';
import { AutomationFlowConfig } from '../../interfaces/automation-flow-config.interface';
import { AutomationRule } from '../../interfaces/automation-rule.interface';
import { AutomationFilterType } from '../../enums/automation-filter-type';
import { AutomationRuleFilterSelection } from '../../interfaces/automation-rule-filter-selection';
import { AutomationDryrunLiveStatus } from '../../enums/automation-dryrun-live-status';

export interface AutomationState {
  flowConfigurations: AutomationFlowConfig[];
  flowRulesLoading: boolean;
  flowRules: AutomationRule[];
  flowRulesTotal: number;
  flowFilters: AutomationRuleFilterSelection;
  dryRunExportLoading: boolean;
  needsRefresh: boolean; 
}

export const initialState: AutomationState = {
  flowConfigurations: [],
  flowRulesLoading: true,
  flowRules: [],
  flowRulesTotal: 0,
  flowFilters: {
    [AutomationFilterType.STATUS]: [],
  },
  dryRunExportLoading: false,
  needsRefresh: false, 
};

const automationReducer = createReducer(
  initialState,
  on(actions.GetFlowConfigurationsSuccess, (state, payload) => ({
    ...state,
    flowConfigurations: payload.configs,
  })),
  on(actions.GetFlowRules, (state, payload) => ({
    ...state,
    flowRulesLoading: true,
    flowRules: [],
    needsRefresh: false, 
  })),
  on(actions.GetFlowRulesSuccess, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
    flowRules: payload.rules,
    flowRulesTotal: payload.total,
  })),
  on(actions.GetFlowRulesFail, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
    flowRules: [],
  })),
  on(actions.SaveRule, (state, payload) => ({
    ...state,
    flowRulesLoading: true,
  })),
  on(actions.SaveRuleSuccess, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
    needsRefresh: true, 
  })),
  on(actions.SaveRuleFail, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
  })),
  on(actions.DeleteRule, (state, payload) => ({
    ...state,
    flowRulesLoading: true,
  })),
  on(actions.DeleteRuleSuccess, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
    needsRefresh: true,
  })),
  on(actions.DeleteRuleFail, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
  })),
  on(actions.SetDryRunStatus, (state, payload) => ({
    ...state,
    flowRulesLoading: true,
  })),
  on(actions.SetDryRunStatusSuccess, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
    flowRules: [
      ...state.flowRules.map(r => {
        if (r.id == payload.id) {
          return {
            ...r,
            dryRun: payload.dryRun,
            statusDate: new Date().toISOString()
          };
        }
        return r;
      }),
    ],
  })),
  on(actions.SetDryRunStatusFail, (state, payload) => ({
    ...state,
    flowRulesLoading: false,
  })),
  on(actions.UpdateRuleStatusFilter, (state, payload) => ({
    ...state,
    flowFilters: {
      ...state.flowFilters,
      [AutomationFilterType.STATUS]: (state.flowFilters[AutomationFilterType.STATUS] as AutomationDryrunLiveStatus[]).includes(payload.option as AutomationDryrunLiveStatus)
        ? (state.flowFilters[AutomationFilterType.STATUS] as AutomationDryrunLiveStatus[]).filter(
            (val: AutomationDryrunLiveStatus) => val !== payload.option
          ) as AutomationDryrunLiveStatus[]
        : [...(state.flowFilters[AutomationFilterType.STATUS] as AutomationDryrunLiveStatus[]), payload.option] as AutomationDryrunLiveStatus[],
    },
  })),
  on(actions.ClearRuleFilter, (state, payload) => ({
    ...state,
    flowFilters: {
      [AutomationFilterType.STATUS]: [],
    },
  })),
  on(actions.ExportDryrunData, (state, payload) => ({
    ...state,
    dryRunExportLoading: true
  })),
  on(actions.ExportDryrunDataSuccess, (state, payload) => ({
    ...state,
    dryRunExportLoading: false
  })),
  on(actions.ExportDryrunDataFailure, (state, payload) => ({
    ...state,
    dryRunExportLoading: false
  })),
);

export function reducer(state: AutomationState | undefined, action: Action): AutomationState {
  return automationReducer(state, action);
}
